import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Modal,
  Table,
  Paper,
  Button,
  TableRow,
  Container,
  TableCell,
  TableBody,
  TableHead,
  Typography,
  TextField,
  TableContainer,
  TableSortLabel,
  Switch,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  IconButton,
  Divider,
  Chip,
  Grid,
  Tooltip,
  CircularProgress,
  Alert,
} from "@mui/material";
import dayjs from "dayjs";
import axios from "axios";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { BASE_URL } from "../../components/config/config";
import { Add, Delete, Edit, Close } from "@mui/icons-material";
import {
  canViewOffers,
  canAddOffer,
  canUpdateOffer,
  canDeleteOffer,
  canActiveInactiveOffer,
  canViewDiscounts,
  canAddDiscount,
  canUpdateDiscount,
  canDeleteDiscount,
  canActiveInactiveDiscount,
} from "../../utility/permissionUtils";

const OffersPage = () => {
  const globalState = useSelector((state) => state.AdminReducer);
  const userPermissions = globalState?.adminDetails?.role_id?.permissions;

  // Offers state
  const [sortKey, setSortKey] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [offers, setOffers] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "CONSUMER",
    fromDate: null,
    toDate: null,
    offerId: null,
  });

  // Discounts state
  const [discounts, setDiscounts] = useState([]);
  const [discountModalOpen, setDiscountModalOpen] = useState(false);
  const [discountFormData, setDiscountFormData] = useState({
    discountId: null,
    clientId: "",
    discountSlabs: [
      {
        minAmount: 0,
        maxAmount: 0,
        discountAmount: 0,
        discountType: "FLAT",
      },
    ],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Permission checks
  const hasOfferAccess = canViewOffers(userPermissions);
  const hasDiscountAccess = canViewDiscounts(userPermissions);
  const canCreateOffer = canAddOffer(userPermissions);
  const canEditOffer = canUpdateOffer(userPermissions);
  const canRemoveOffer = canDeleteOffer(userPermissions);
  const canToggleOfferStatus = canActiveInactiveOffer(userPermissions);
  const canCreateDiscount = canAddDiscount(userPermissions);
  const canEditDiscount = canUpdateDiscount(userPermissions);
  const canRemoveDiscount = canDeleteDiscount(userPermissions);
  const canToggleDiscountStatus = canActiveInactiveDiscount(userPermissions);

  // Common functions
  const handleSort = (key) => {
    const isAsc = sortKey === key && sortOrder === "asc";
    setSortKey(key);
    setSortOrder(isAsc ? "desc" : "asc");
  };

  const clearFilters = () => {
    setStartDate(null);
    setEndDate(null);
  };

  const showAlert = (message, type) => {
    if (type === "error") {
      setError(message);
    } else {
      setSuccess(message);
    }
    setTimeout(() => {
      setError(null);
      setSuccess(null);
    }, 5000);
  };

  // Offers functions
  const openModal = (offer = null) => {
    if (offer) {
      if (!canEditOffer) {
        showAlert("You don't have permission to edit offers", "error");
        return;
      }
      setFormData({
        offerId: offer._id,
        title: offer.title,
        description: offer.description,
        category: offer.category,
        fromDate: dayjs(offer.fromDate),
        toDate: dayjs(offer.toDate),
      });
    } else {
      if (!canCreateOffer) {
        showAlert("You don't have permission to create offers", "error");
        return;
      }
      setFormData({
        offerId: null,
        title: "",
        description: "",
        category: "CONSUMER",
        fromDate: null,
        toDate: null,
      });
    }
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const handleStatusChange = async (offerId, isActive) => {
    if (!canToggleOfferStatus) {
      showAlert("You don't have permission to change offer status", "error");
      return;
    }

    try {
      await axios.post(`${BASE_URL}center/offercode/editstatus`, {
        offerId: offerId,
        isActive: isActive ? "true" : "false",
      });
      getAllOffers();
      showAlert("Offer status updated successfully", "success");
    } catch (error) {
      console.error("Error updating status:", error);
      showAlert("Failed to update offer status", "error");
    }
  };

  const handleSubmit = async () => {
    if (formData.offerId && !canEditOffer) {
      showAlert("You don't have permission to update offers", "error");
      return;
    }
    if (!formData.offerId && !canCreateOffer) {
      showAlert("You don't have permission to create offers", "error");
      return;
    }

    try {
      const url = formData.offerId
        ? `${BASE_URL}center/update-offercode`
        : `${BASE_URL}center/add-offer`;

      const payload = {
        ...formData,
        ...(formData.offerId && { offerId: formData.offerId }),
      };

      await axios.post(url, payload);
      getAllOffers();
      closeModal();
      showAlert(
        formData.offerId
          ? "Offer updated successfully"
          : "Offer created successfully",
        "success"
      );
    } catch (error) {
      console.error("Error submitting offer:", error);
      showAlert("Failed to submit offer", "error");
    }
  };

  const handleDelete = async (offerId) => {
    if (!canRemoveOffer) {
      showAlert("You don't have permission to delete offers", "error");
      return;
    }

    try {
      await axios.delete(`${BASE_URL}center/offercode/delete/${offerId}`);
      getAllOffers();
      showAlert("Offer deleted successfully", "success");
    } catch (error) {
      console.error("Error deleting offer:", error);
      showAlert("Failed to delete offer", "error");
    }
  };

  // Discounts functions
  const getAllClients = async () => {
    try {
      const response = await axios.get(`${BASE_URL}admin/get-all-clients`);
      setClients(response?.data?.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
      showAlert("Failed to fetch clients", "error");
    }
  };

  const openDiscountModal = (discount = null) => {
    if (discount) {
      if (!canEditDiscount) {
        showAlert("You don't have permission to edit discounts", "error");
        return;
      }
      setDiscountFormData({
        discountId: discount._id,
        clientId: discount.clientId,
        discountSlabs: discount.discountSlabs,
        isActive: discount.isActive,
      });
    } else {
      if (!canCreateDiscount) {
        showAlert("You don't have permission to create discounts", "error");
        return;
      }
      setDiscountFormData({
        discountId: null,
        clientId: "",
        discountSlabs: [
          {
            minAmount: 0,
            maxAmount: 0,
            discountAmount: 0,
            discountType: "FLAT",
          },
        ],
        isActive: true,
      });
    }
    setDiscountModalOpen(true);
  };

  const closeDiscountModal = () => setDiscountModalOpen(false);

  const handleDiscountSlabChange = (index, field, value) => {
    const updatedSlabs = [...discountFormData.discountSlabs];
    updatedSlabs[index][field] = value;
    setDiscountFormData({
      ...discountFormData,
      discountSlabs: updatedSlabs,
    });
  };

  const addDiscountSlab = () => {
    setDiscountFormData({
      ...discountFormData,
      discountSlabs: [
        ...discountFormData.discountSlabs,
        {
          minAmount: 0,
          maxAmount: 0,
          discountAmount: 0,
          discountType: "FLAT",
        },
      ],
    });
  };

  const removeDiscountSlab = (index) => {
    const updatedSlabs = discountFormData.discountSlabs.filter(
      (_, i) => i !== index
    );
    setDiscountFormData({
      ...discountFormData,
      discountSlabs: updatedSlabs,
    });
  };

  const handleDiscountSubmit = async () => {
    if (discountFormData.discountId && !canEditDiscount) {
      showAlert("You don't have permission to update discounts", "error");
      return;
    }
    if (!discountFormData.discountId && !canCreateDiscount) {
      showAlert("You don't have permission to create discounts", "error");
      return;
    }

    setLoading(true);
    try {
      const url = discountFormData.discountId
        ? `${BASE_URL}admin/update-discount/${discountFormData.discountId}`
        : `${BASE_URL}admin/create-discount`;

      const payload = {
        clientId: discountFormData.clientId,
        discountSlabs: discountFormData.discountSlabs,
        isActive: discountFormData.isActive || true,
      };

      const method = discountFormData.discountId ? "patch" : "post";
      await axios[method](url, payload);
      getAllDiscounts();
      closeDiscountModal();
      showAlert(
        discountFormData.discountId
          ? "Discount updated successfully"
          : "Discount created successfully",
        "success"
      );
    } catch (error) {
      console.error("Error submitting discount:", error);
      showAlert("Failed to submit discount", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDiscountStatusChange = async (discountId, isActive) => {
    if (!canToggleDiscountStatus) {
      showAlert("You don't have permission to change discount status", "error");
      return;
    }

    try {
      await axios.patch(`${BASE_URL}admin/update-discount/${discountId}`, {
        isActive: isActive,
        clientId: discountFormData.clientId,
        discountSlabs: discountFormData.discountSlabs,
      });
      getAllDiscounts();
      showAlert("Discount status updated successfully", "success");
    } catch (error) {
      console.error("Error updating discount status:", error);
      showAlert("Failed to update discount status", "error");
    }
  };

  const handleDiscountDelete = async (discountId) => {
    if (!canRemoveDiscount) {
      showAlert("You don't have permission to delete discounts", "error");
      return;
    }

    try {
      await axios.delete(`${BASE_URL}admin/delete-discount/${discountId}`);
      getAllDiscounts();
      showAlert("Discount deleted successfully", "success");
    } catch (error) {
      console.error("Error deleting discount:", error);
      showAlert("Failed to delete discount", "error");
    }
  };

  // Data fetching
  const getAllOffers = async () => {
    if (!hasOfferAccess) return;

    try {
      const response = await axios.get(`${BASE_URL}center/get-offer/ADMIN`);
      setOffers(response?.data?.data);
    } catch (error) {
      console.error("Error fetching offers:", error);
      showAlert("Failed to fetch offers", "error");
    }
  };

  const getAllDiscounts = async () => {
    if (!hasDiscountAccess) return;

    try {
      const response = await axios.get(`${BASE_URL}admin/get-all-discount`);
      setDiscounts(response?.data?.data);
    } catch (error) {
      console.error("Error fetching discounts:", error);
      showAlert("Failed to fetch discounts", "error");
    }
  };

  // Filtering and sorting
  const filteredOffers = offers?.filter((offer) => {
    const createdAt = dayjs(offer.createdAt);
    const start = startDate ? dayjs(startDate) : null;
    const end = endDate ? dayjs(endDate) : null;
    if (start && createdAt.isBefore(start, "day")) return false;
    if (end && createdAt.isAfter(end, "day")) return false;
    return true;
  });

  const sortedOffers = [...filteredOffers]?.sort((a, b) => {
    const orderMultiplier = sortOrder === "asc" ? 1 : -1;
    if (sortKey === "toDate")
      return orderMultiplier * (new Date(a.toDate) - new Date(b.toDate));
    return orderMultiplier * (new Date(a[sortKey]) - new Date(b[sortKey]));
  });

  useEffect(() => {
    getAllOffers();
    getAllDiscounts();
    getAllClients();
  }, []);

  // If user has no access to either offers or discounts, show access denied
  if (!hasOfferAccess && !hasDiscountAccess) {
    return (
      <Container maxWidth="xl" className="py-8">
        <Alert severity="error">
          You don't have permission to access Offers & Discounts section.
        </Alert>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="xl" className="py-8">
        {/* Alerts */}
        {error && (
          <Alert
            severity="error"
            className="mb-4"
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            severity="success"
            className="mb-4"
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}

        {/* Offers Section */}
        {hasOfferAccess && (
          <Paper elevation={3} className="p-6 mb-8">
            <div className="flex justify-between items-center mb-6">
              <Typography variant="h5" className="font-bold text-gray-800">
                Offers Management
              </Typography>
              {canCreateOffer && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Add />}
                  onClick={() => openModal()}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Create Offer
                </Button>
              )}
            </div>

            <div className="flex gap-4 mb-6">
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={setStartDate}
                renderInput={(params) => (
                  <TextField {...params} className="w-full" size="small" />
                )}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={setEndDate}
                renderInput={(params) => (
                  <TextField {...params} className="w-full" size="small" />
                )}
              />
              <Button
                variant="outlined"
                onClick={clearFilters}
                className="h-10"
              >
                Clear Filters
              </Button>
            </div>

            {sortedOffers?.length === 0 ? (
              <Typography variant="body1" className="text-gray-500">
                No offers available
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead className="bg-gray-100">
                    <TableRow>
                      <TableCell>SN</TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortKey === "createdAt"}
                          direction={
                            sortKey === "createdAt" ? sortOrder : "asc"
                          }
                          onClick={() => handleSort("createdAt")}
                        >
                          Created Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortKey === "toDate"}
                          direction={sortKey === "toDate" ? sortOrder : "asc"}
                          onClick={() => handleSort("toDate")}
                        >
                          Expiration Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Status</TableCell>
                      {(canEditOffer || canRemoveOffer) && (
                        <TableCell>Actions</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedOffers.map((offer, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{offer?.title}</TableCell>
                        <TableCell className="max-w-xs truncate">
                          {offer?.description}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={offer?.category}
                            color={
                              offer?.category === "CONSUMER"
                                ? "primary"
                                : "secondary"
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {dayjs(offer?.createdAt).format("MMM D, YYYY")}
                        </TableCell>
                        <TableCell>
                          {dayjs(offer?.toDate).format("MMM D, YYYY")}
                        </TableCell>
                        <TableCell>
                          {canToggleOfferStatus ? (
                            <Switch
                              checked={offer?.isActive}
                              onChange={(event) =>
                                handleStatusChange(
                                  offer._id,
                                  event.target.checked
                                )
                              }
                              color="primary"
                            />
                          ) : (
                            <Chip
                              label={offer?.isActive ? "Active" : "Inactive"}
                              color={offer?.isActive ? "success" : "default"}
                              size="small"
                            />
                          )}
                        </TableCell>
                        {(canEditOffer || canRemoveOffer) && (
                          <TableCell>
                            <div className="flex gap-2">
                              {canEditOffer && (
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={() => openModal(offer)}
                                    className="text-blue-500"
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {canRemoveOffer && (
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDelete(offer._id)}
                                    className="text-red-500"
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}

        {/* Discounts Section */}
        {hasDiscountAccess && (
          <Paper elevation={3} className="p-6">
            <div className="flex justify-between items-center mb-6">
              <Typography variant="h5" className="font-bold text-gray-800">
                Discounts Management
              </Typography>
              {canCreateDiscount && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Add />}
                  onClick={() => openDiscountModal()}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Create Discount
                </Button>
              )}
            </div>

            {discounts?.length === 0 ? (
              <Typography variant="body1" className="text-gray-500">
                No discounts available
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead className="bg-gray-100">
                    <TableRow>
                      <TableCell>SN</TableCell>
                      <TableCell>Client Name</TableCell>
                      <TableCell>Client QR</TableCell>
                      <TableCell>Discount Slabs</TableCell>
                      <TableCell>Created At</TableCell>
                      <TableCell>Created By</TableCell>
                      <TableCell>Updated At</TableCell>
                      <TableCell>Updated By</TableCell>
                      <TableCell>Status</TableCell>
                      {(canEditDiscount || canRemoveDiscount) && (
                        <TableCell>Actions</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {discounts.map((discount, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{discount?.client_name}</TableCell>
                        <TableCell>{discount?.qr_details?.qr_id}</TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            {discount?.discountSlabs?.map((slab, i) => (
                              <div key={i} className="flex items-center gap-2">
                                <span className="text-sm font-medium">
                                  {slab.minAmount} - {slab.maxAmount}:
                                </span>
                                <span className="text-sm">
                                  {slab.discountAmount}{" "}
                                  {slab.discountType === "FLAT" ? "₹" : "%"}
                                </span>
                              </div>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          {dayjs(discount?.createdAt).format("MMM D, YYYY")}
                        </TableCell>
                        <TableCell>{discount?.createdBy || "-"}</TableCell>
                        <TableCell>
                          {dayjs(discount?.updatedAt).format("MMM D, YYYY")}
                        </TableCell>
                        <TableCell>{discount?.updatedBy || "-"}</TableCell>
                        <TableCell>
                          {canToggleDiscountStatus ? (
                            <Switch
                              checked={discount?.isActive}
                              onChange={(event) =>
                                handleDiscountStatusChange(
                                  discount._id,
                                  event.target.checked
                                )
                              }
                              color="primary"
                            />
                          ) : (
                            <Chip
                              label={discount?.isActive ? "Active" : "Inactive"}
                              color={discount?.isActive ? "success" : "default"}
                              size="small"
                            />
                          )}
                        </TableCell>
                        {(canEditDiscount || canRemoveDiscount) && (
                          <TableCell>
                            <div className="flex gap-2">
                              {canEditDiscount && (
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={() => openDiscountModal(discount)}
                                    className="text-blue-500"
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {canRemoveDiscount && (
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleDiscountDelete(discount._id)
                                    }
                                    className="text-red-500"
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}

        {/* Offer Modal */}
        <Modal open={modalOpen} onClose={closeModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 500,
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
            }}
          >
            <div className="flex justify-between items-center mb-4">
              <Typography variant="h6" className="font-bold">
                {formData.offerId ? "Edit Offer" : "Create Offer"}
              </Typography>
              <IconButton onClick={closeModal} size="small">
                <Close />
              </IconButton>
            </div>
            <Divider className="mb-4" />
            <div className="space-y-4">
              <TextField
                label="Title"
                fullWidth
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                size="small"
              />
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                size="small"
              />
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  label="Category"
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                >
                  <MenuItem value="CONSUMER">Consumer</MenuItem>
                  <MenuItem value="BUSINESS">Business</MenuItem>
                </Select>
              </FormControl>
              <div className="grid grid-cols-2 gap-4">
                <DatePicker
                  label="Start Date"
                  value={formData.fromDate}
                  onChange={(newValue) =>
                    setFormData({ ...formData, fromDate: newValue })
                  }
                  renderInput={(params) => (
                    <TextField {...params} fullWidth size="small" />
                  )}
                />
                <DatePicker
                  label="End Date"
                  value={formData.toDate}
                  onChange={(newValue) =>
                    setFormData({ ...formData, toDate: newValue })
                  }
                  renderInput={(params) => (
                    <TextField {...params} fullWidth size="small" />
                  )}
                />
              </div>
            </div>
            <Divider className="my-4" />
            <div className="flex justify-end gap-2">
              <Button variant="outlined" onClick={closeModal}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {formData.offerId ? "Update" : "Create"}
              </Button>
            </div>
          </Box>
        </Modal>

        {/* Discount Modal */}
        <Modal open={discountModalOpen} onClose={closeDiscountModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 600,
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <div className="flex justify-between items-center mb-4">
              <Typography variant="h6" className="font-bold">
                {discountFormData.discountId
                  ? "Edit Discount"
                  : "Create Discount"}
              </Typography>
              <IconButton onClick={closeDiscountModal} size="small">
                <Close />
              </IconButton>
            </div>
            <Divider className="mb-4" />
            <div className="space-y-4">
              <FormControl fullWidth size="small">
                <InputLabel>Client</InputLabel>
                <Select
                  value={discountFormData.clientId}
                  label="Client"
                  onChange={(e) =>
                    setDiscountFormData({
                      ...discountFormData,
                      clientId: e.target.value,
                    })
                  }
                >
                  {clients.map((client) => (
                    <MenuItem key={client._id} value={client.client_id}>
                      {client.client_name} ({client.client_id})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Typography variant="subtitle1" className="font-medium">
                Discount Slabs
              </Typography>

              {discountFormData.discountSlabs.map((slab, index) => (
                <div
                  key={index}
                  className="p-4 border rounded-lg space-y-3 relative"
                >
                  {index > 0 && (
                    <IconButton
                      size="small"
                      onClick={() => removeDiscountSlab(index)}
                      className="absolute top-1 right-1 text-red-500"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Minimum Amount"
                        type="number"
                        fullWidth
                        value={slab.minAmount}
                        onChange={(e) =>
                          handleDiscountSlabChange(
                            index,
                            "minAmount",
                            parseFloat(e.target.value)
                          )
                        }
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Maximum Amount"
                        type="number"
                        fullWidth
                        value={slab.maxAmount}
                        onChange={(e) =>
                          handleDiscountSlabChange(
                            index,
                            "maxAmount",
                            parseFloat(e.target.value)
                          )
                        }
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Discount Amount"
                        type="number"
                        fullWidth
                        value={slab.discountAmount}
                        onChange={(e) =>
                          handleDiscountSlabChange(
                            index,
                            "discountAmount",
                            parseFloat(e.target.value)
                          )
                        }
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Discount Type</InputLabel>
                        <Select
                          value={slab.discountType}
                          label="Discount Type"
                          onChange={(e) =>
                            handleDiscountSlabChange(
                              index,
                              "discountType",
                              e.target.value
                            )
                          }
                        >
                          <MenuItem value="FLAT">Flat (₹)</MenuItem>
                          <MenuItem value="PERCENTAGE">Percentage (%)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </div>
              ))}

              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={addDiscountSlab}
                fullWidth
              >
                Add Slab
              </Button>
            </div>
            <Divider className="my-4" />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outlined" onClick={closeDiscountModal}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleDiscountSubmit}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : discountFormData.discountId ? (
                  "Update"
                ) : (
                  "Create"
                )}
              </Button>
            </div>
          </Box>
        </Modal>
      </Container>
    </LocalizationProvider>
  );
};

export default OffersPage;
